import type { IContract } from '~/types/contract/models'
import type { TEntityId } from '~/types/models'

/**
 * Trim each contract's nested `contractDocuments` / `contractNotes` to only the
 * items belonging to the given silo (opportunity / order / company).
 *
 * A contract document/note is owned by a contract AND by exactly one silo entity
 * (the silo it was created in), so a silo overview must surface only its own
 * silo's items. This is the server-side counterpart of what the client used to
 * do per-silo (see issue #83) — it lives here, in overview assembly, so the
 * client can trust the response as-is.
 *
 * Returns shallow clones with freshly `.filter()`ed arrays — the input contracts
 * are references into the cached canonical contract list, so we must never mutate
 * their arrays (other silos' overviews reuse the same objects).
 */
export function _trimContractChildArraysToSilo(
  contracts: IContract[],
  siloEntityName: 'opportunity' | 'order' | 'company',
  siloRootId: TEntityId
): IContract[] {
  return contracts.map((contract) => ({
    ...contract,
    contractDocuments: (contract.contractDocuments ?? []).filter(
      (d) => d[siloEntityName]?.id === siloRootId
    ),
    contractNotes: (contract.contractNotes ?? []).filter(
      (n) => n[siloEntityName]?.id === siloRootId
    ),
  }))
}
